'use client';

import { c as _c } from "react/compiler-runtime";
import { type PostFeedInput } from '@shared/all/validators';
import { useUserIdentity } from '@shared/utils/context/UserContext';
import useLocalPreference from '@shared/utils/hooks/useLocalPreference';
import useNavigator from '@shared/utils/hooks/useNavigator';
import usePostsShared from '@shared/utils/hooks/usePosts';
import useSocket from '@shared/utils/hooks/useSocket';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useEffectEvent } from 'react';
import { client } from '../../utils/trpc';
import DataMode from './DataMode';
import MediaSearchMode from './MediaSearchMode';
import PostMode from './PostMode';
import SortByFactiiiMode from './SortByFactiiiMode';
type Props = {
  params: PostFeedInput;
  manualLoading?: boolean;
  hideEndReachedMessage?: boolean;
  uniqueId?: string;
  isFullLength: boolean;
  disableLink: boolean;
  disableTracking?: boolean;
};
const Posts = props => {
  const $ = _c(37);
  const {
    navigatorState,
    setLogoClicked
  } = useNavigator();
  const {
    updatedLocalPreference,
    localPreferenceState
  } = useLocalPreference();
  const currentUser = useUserIdentity();
  const queryClient = useQueryClient();
  const {
    queryKey,
    appendReply,
    updateReplies,
    updateVote
  } = usePostsShared(client, props.uniqueId);
  const {
    onRepliesCount,
    onNewReply,
    onVoteCount
  } = useSocket();
  let t0;
  if ($[0] !== updateReplies) {
    t0 = (repliesCount, postId) => {
      updateReplies(repliesCount, postId);
    };
    $[0] = updateReplies;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleRepliesCount = useEffectEvent(t0);
  let t1;
  if ($[2] !== appendReply) {
    t1 = reply => {
      appendReply(reply);
    };
    $[2] = appendReply;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleNewReply = useEffectEvent(t1);
  let t2;
  if ($[4] !== updateVote) {
    t2 = (totalVoteCount, postId_0) => {
      updateVote(totalVoteCount, postId_0);
    };
    $[4] = updateVote;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleVoteCount = useEffectEvent(t2);
  let t3;
  if ($[6] !== handleNewReply || $[7] !== handleRepliesCount || $[8] !== handleVoteCount || $[9] !== onNewReply || $[10] !== onRepliesCount || $[11] !== onVoteCount) {
    t3 = () => {
      const cleanupRepliesCount = onRepliesCount((repliesCount_0, postId_1) => handleRepliesCount(repliesCount_0, postId_1));
      const cleanupNewReply = onNewReply(reply_0 => handleNewReply(reply_0));
      const cleanupVoteCount = onVoteCount((totalVoteCount_0, postId_2) => handleVoteCount(totalVoteCount_0, postId_2));
      return () => {
        cleanupRepliesCount();
        cleanupNewReply();
        cleanupVoteCount();
      };
    };
    $[6] = handleNewReply;
    $[7] = handleRepliesCount;
    $[8] = handleVoteCount;
    $[9] = onNewReply;
    $[10] = onRepliesCount;
    $[11] = onVoteCount;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  useEffect(t3, t4);
  let t5;
  if ($[14] !== currentUser.isAuthenticated || $[15] !== updatedLocalPreference) {
    t5 = () => {
      if (currentUser.isAuthenticated === false) {
        updatedLocalPreference({
          postModeType: "explore"
        });
      }
    };
    $[14] = currentUser.isAuthenticated;
    $[15] = updatedLocalPreference;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== currentUser.isAuthenticated) {
    t6 = [currentUser.isAuthenticated];
    $[17] = currentUser.isAuthenticated;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  useEffect(t5, t6);
  let t7;
  if ($[19] !== navigatorState.logoClicked || $[20] !== queryClient || $[21] !== queryKey || $[22] !== setLogoClicked) {
    t7 = () => {
      if (navigatorState.logoClicked) {
        queryClient.resetQueries({
          queryKey
        });
        queryClient.resetQueries({
          queryKey: ["RelatedFactiiiCountGroups"]
        });
        setLogoClicked(false);
      }
    };
    $[19] = navigatorState.logoClicked;
    $[20] = queryClient;
    $[21] = queryKey;
    $[22] = setLogoClicked;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== navigatorState.logoClicked || $[25] !== queryKey) {
    t8 = [navigatorState.logoClicked, queryKey];
    $[24] = navigatorState.logoClicked;
    $[25] = queryKey;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  useEffect(t7, t8);
  const isSearch = props.params.source === "search";
  let t9;
  if ($[27] !== isSearch || $[28] !== localPreferenceState || $[29] !== props.disableLink || $[30] !== props.disableTracking || $[31] !== props.hideEndReachedMessage || $[32] !== props.isFullLength || $[33] !== props.manualLoading || $[34] !== props.params || $[35] !== props.uniqueId) {
    t9 = <section className="bg-background-primary">{!isSearch && localPreferenceState.displayMode === "mediaSearch" && localPreferenceState.mediaSearch ? <MediaSearchMode params={props.params} uniqueId={props.uniqueId} /> : !isSearch && localPreferenceState.postFilterType && localPreferenceState.displayMode === "data" ? <DataMode params={props.params} uniqueId={props.uniqueId} /> : !isSearch && localPreferenceState.displayMode === "sortByFactiii" ? <SortByFactiiiMode params={props.params} uniqueId={props.uniqueId} /> : <PostMode params={props.params} isFullLength={props.isFullLength} disableLink={props.disableLink} disableTracking={props.disableTracking} manualLoading={props.manualLoading} hideEndReachedMessage={props.hideEndReachedMessage} uniqueId={props.uniqueId} />}</section>;
    $[27] = isSearch;
    $[28] = localPreferenceState;
    $[29] = props.disableLink;
    $[30] = props.disableTracking;
    $[31] = props.hideEndReachedMessage;
    $[32] = props.isFullLength;
    $[33] = props.manualLoading;
    $[34] = props.params;
    $[35] = props.uniqueId;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  return t9;
};
export default Posts;